import {
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { LADDERS, LADDER_IDS, ladderOf } from "@/shared/ladders";
import { DEFAULT_SETTINGS, type Group, type Question, type Settings } from "@/shared/types";

interface Payload {
  groups: Group[];
  questions: Question[];
  settings: Settings;
}

/** The password form shown until /api/admin stops answering 401. */
function AdminGate({
  note,
  setNote,
  onUnlocked,
}: {
  note: string | null;
  setNote: (n: string | null) => void;
  onUnlocked: () => Promise<void>;
}) {
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim() || checking) return;
    setChecking(true);
    setNote(null);
    try {
      await api.post("/api/admin/login", { password: password.trim() });
      await onUnlocked();
    } catch (err) {
      setNote(err instanceof Error ? err.message : "That didn't work.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <Shell>
      <div className="flex min-h-[60vh] items-center justify-center">
        <form onSubmit={submit} className="w-full max-w-sm">
          <h1 className="text-2xl font-semibold tracking-[-0.025em]">Admin</h1>
          <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
            This page edits the live catalogue. It wants the admin password.
          </p>
          <div className="mt-6 flex gap-2">
            <Input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              autoComplete="current-password"
            />
            <Button type="submit" disabled={!password.trim() || checking}>
              {checking ? <Loader2 className="size-4 animate-spin" /> : "Enter"}
            </Button>
          </div>
          {note && <p className="mt-3 text-[13px] text-destructive">{note}</p>}
        </form>
      </div>
    </Shell>
  );
}

export function Admin() {
  const [data, setData] = useState<Payload | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  // null = unknown (loading), true = the server wants a password first.
  const [locked, setLocked] = useState<boolean | null>(null);

  const reload = useCallback(async () => {
    setData(await api.get<Payload>("/api/admin"));
    setLocked(false);
  }, []);

  useEffect(() => {
    reload().catch((e) => {
      if (e instanceof ApiError && e.status === 401) setLocked(true);
      else if (e instanceof ApiError && e.status === 404) {
        setLocked(true);
        setNote("Admin is disabled — ADMIN_PASSWORD isn't set on the server.");
      } else setNote("Couldn't load the catalogue.");
    });
  }, [reload]);

  const act = useCallback(
    async (fn: () => Promise<unknown>, message?: string) => {
      setBusy(true);
      setNote(null);
      try {
        await fn();
        await reload();
        if (message) {
          setNote(message);
          setTimeout(() => setNote(null), 2500);
        }
      } catch (e) {
        // A 401 mid-session means the admin cookie expired — back to the gate.
        if (e instanceof ApiError && e.status === 401) setLocked(true);
        setNote(e instanceof Error ? e.message : "That didn't work.");
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  const byGroup = useMemo(() => {
    const out = new Map<string, Question[]>();
    for (const q of data?.questions ?? []) {
      const list = out.get(q.groupId) ?? [];
      list.push(q);
      out.set(q.groupId, list);
    }
    return out;
  }, [data?.questions]);

  if (locked) {
    return <AdminGate note={note} setNote={setNote} onUnlocked={reload} />;
  }

  if (!data) {
    return (
      <Shell wide>
        <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" /> Loading the catalogue…
        </div>
      </Shell>
    );
  }

  const { groups, settings } = data;

  return (
    <Shell wide>
      <div className="py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-[-0.025em]">Admin</h1>
            <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
              The question catalogue is data, not code. Everything here takes effect on the next
              save anyone makes — and deleting takes every answer given to it with it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {note && <span className="text-[13px] text-brand">{note}</span>}
            {busy && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
          </div>
        </div>
      </div>

      {/* Settings */}
      <section className="mb-10 rounded-2xl border border-border/70 bg-card/40 p-6 backdrop-blur-sm">
        <h2 className="text-[17px] font-semibold tracking-tight">Matching</h2>
        <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <SettingField
            label="Match threshold"
            help="A pair must score at least this to be told about each other. Two random people score about 55%."
            value={settings.matchThreshold}
            min={0.1}
            max={0.99}
            step={0.01}
            format={(v) => `${Math.round(v * 100)}%`}
            onCommit={(matchThreshold) =>
              act(() => api.put("/api/admin/settings", { matchThreshold }), "Saved")
            }
          />
          <SettingField
            label="Settle window"
            help="Hours an edit must sit untouched before it counts. 0 matches immediately."
            value={settings.settleHours}
            min={0}
            max={168}
            step={1}
            format={(v) => `${v}h`}
            onCommit={(settleHours) =>
              act(() => api.put("/api/admin/settings", { settleHours }), "Saved")
            }
          />
          <SettingField
            label="Minimum completeness"
            help="Fraction of active questions someone must answer before they can be matched at all."
            value={settings.minCompleteness}
            min={0}
            max={1}
            step={0.05}
            format={(v) => `${Math.round(v * 100)}%`}
            onCommit={(minCompleteness) =>
              act(() => api.put("/api/admin/settings", { minCompleteness }), "Saved")
            }
          />
          <SettingField
            label="Minimum overlap"
            help="Questions two people must both have answered before a score is shown at all."
            value={settings.minOverlap}
            min={1}
            max={100}
            step={1}
            format={(v) => `${v}q`}
            onCommit={(minOverlap) =>
              act(() => api.put("/api/admin/settings", { minOverlap }), "Saved")
            }
          />
        </div>

        <div className="mt-6 flex flex-wrap gap-2.5 border-t border-border/60 pt-5">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => act(() => api.post("/api/admin/recompute"), "Recomputed")}
          >
            <RefreshCw /> Recompute all profiles
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => act(() => api.post("/api/admin/reseed"), "Catalogue re-seeded")}
          >
            <Sparkles /> Re-seed catalogue
          </Button>
          <span className="self-center text-[12.5px] text-muted-foreground">
            Re-seeding merges on key — existing answers survive.
          </span>
        </div>
      </section>

      {/* Groups */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[17px] font-semibold tracking-tight">
          {groups.length} groups · {data.questions.length} questions
        </h2>
        <Button
          className="rounded-full"
          onClick={() => act(() => api.post("/api/admin/groups", { label: "New group" }))}
        >
          <Plus /> Add group
        </Button>
      </div>

      <div className="space-y-3 pb-24">
        {groups.map((g, i) => {
          const questions = byGroup.get(g.id) ?? [];
          const open = openGroup === g.id;
          return (
            <div
              key={g.id}
              className="rounded-2xl border border-border/70 bg-card/40 backdrop-blur-sm"
            >
              <GroupRow
                group={g}
                questionCount={questions.length}
                open={open}
                canMoveUp={i > 0}
                canMoveDown={i < groups.length - 1}
                onToggle={() => setOpenGroup(open ? null : g.id)}
                onPatch={(patch) => act(() => api.put(`/api/admin/groups/${g.id}`, patch))}
                onMove={(dir) => {
                  const ids = groups.map((x) => x.id);
                  const j = i + dir;
                  [ids[i], ids[j]] = [ids[j]!, ids[i]!];
                  return act(() => api.post("/api/admin/groups/reorder", { ids }));
                }}
                onDelete={() =>
                  act(
                    () => api.del(`/api/admin/groups/${g.id}`),
                    `Deleted "${g.label}" and ${questions.length} question(s)`,
                  )
                }
              />

              {open && (
                <div className="border-t border-border/60 px-4 py-3">
                  {questions.map((q, qi) => (
                    <QuestionRow
                      key={q.id}
                      question={q}
                      groups={groups}
                      allQuestions={data.questions}
                      canMoveUp={qi > 0}
                      canMoveDown={qi < questions.length - 1}
                      onPatch={(patch) =>
                        act(() => api.put(`/api/admin/questions/${q.id}`, patch))
                      }
                      onLink={(targetId) =>
                        act(() =>
                          api.put(`/api/admin/questions/${q.id}/link`, { targetId }),
                        )
                      }
                      onMove={(dir) => {
                        const ids = questions.map((x) => x.id);
                        const j = qi + dir;
                        [ids[qi], ids[j]] = [ids[j]!, ids[qi]!];
                        return act(() => api.post("/api/admin/questions/reorder", { ids }));
                      }}
                      onDelete={() => act(() => api.del(`/api/admin/questions/${q.id}`))}
                    />
                  ))}

                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 rounded-full text-muted-foreground"
                    onClick={() =>
                      act(() =>
                        api.post("/api/admin/questions", {
                          groupId: g.id,
                          prompt: "New question",
                        }),
                      )
                    }
                  >
                    <Plus /> Add a question to {g.label}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------

function SettingField({
  label,
  help,
  value,
  min,
  max,
  step,
  format,
  onCommit,
}: {
  label: string;
  help: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onCommit: (v: number) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <label className="text-[13.5px] font-medium">{label}</label>
        <span className="text-[13px] tabular-nums text-brand">{format(local)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={local}
        onChange={(e) => setLocal(Number(e.target.value))}
        onMouseUp={() => onCommit(local)}
        onTouchEnd={() => onCommit(local)}
        onKeyUp={() => onCommit(local)}
        className="mt-2 w-full accent-[var(--brand)]"
      />
      <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">{help}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Commits on blur rather than per keystroke — every save is a round trip. */
function LiveInput({
  value,
  onCommit,
  className,
  ...rest
}: {
  value: string;
  onCommit: (v: string) => void;
} & Omit<React.ComponentProps<typeof Input>, "value" | "onChange">) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <Input
      {...rest}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => local !== value && onCommit(local)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") setLocal(value);
      }}
      className={cn("bg-background/60", className)}
    />
  );
}

function GroupRow({
  group,
  questionCount,
  open,
  canMoveUp,
  canMoveDown,
  onToggle,
  onPatch,
  onMove,
  onDelete,
}: {
  group: Group;
  questionCount: number;
  open: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onToggle: () => void;
  onPatch: (patch: Partial<Group>) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-2 p-3">
      <div className="flex flex-col">
        <button
          type="button"
          disabled={!canMoveUp}
          onClick={() => onMove(-1)}
          className="text-muted-foreground/50 hover:text-foreground disabled:opacity-20"
          aria-label="Move group up"
        >
          <ChevronUp className="size-3.5" />
        </button>
        <button
          type="button"
          disabled={!canMoveDown}
          onClick={() => onMove(1)}
          className="text-muted-foreground/50 hover:text-foreground disabled:opacity-20"
          aria-label="Move group down"
        >
          <ChevronDown className="size-3.5" />
        </button>
      </div>

      <LiveInput
        value={group.emoji}
        onCommit={(emoji) => onPatch({ emoji })}
        className="w-14 text-center"
        aria-label="Group emoji"
      />
      <LiveInput
        value={group.label}
        onCommit={(label) => onPatch({ label })}
        className="w-48 font-medium"
        aria-label="Group name"
      />
      <LiveInput
        value={group.blurb}
        onCommit={(blurb) => onPatch({ blurb })}
        placeholder="Blurb shown above the sliders"
        className="min-w-0 flex-1"
        aria-label="Group blurb"
      />

      <button
        type="button"
        title={
          group.publicBreakdown
            ? "Score shown on public profiles"
            : "Score hidden from public profiles"
        }
        onClick={() => onPatch({ publicBreakdown: !group.publicBreakdown })}
        className={cn(
          "grid size-8 place-items-center rounded-full transition-colors",
          group.publicBreakdown
            ? "text-brand hover:bg-brand/10"
            : "text-muted-foreground/50 hover:bg-accent",
        )}
      >
        {group.publicBreakdown ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
      </button>

      <button
        type="button"
        onClick={onToggle}
        className="rounded-full bg-muted px-3 py-1 text-[12.5px] tabular-nums text-muted-foreground hover:bg-accent"
      >
        {questionCount} {open ? "▲" : "▼"}
      </button>

      {confirming ? (
        <span className="flex items-center gap-1.5">
          <Button size="sm" variant="destructive" className="rounded-full" onClick={onDelete}>
            Delete {questionCount}q
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full"
            onClick={() => setConfirming(false)}
          >
            No
          </Button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          aria-label="Delete group"
          className="grid size-8 place-items-center rounded-full text-muted-foreground/40 hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="size-4" />
        </button>
      )}
    </div>
  );
}

function QuestionRow({
  question,
  groups,
  allQuestions,
  canMoveUp,
  canMoveDown,
  onPatch,
  onLink,
  onMove,
  onDelete,
}: {
  question: Question;
  groups: Group[];
  /** The whole catalogue — a question can be paired across groups. */
  allQuestions: Question[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  onPatch: (patch: Partial<Question>) => void;
  onLink: (targetId: string | null) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const linked = allQuestions.find((q) => q.id === question.linkedQuestionId) ?? null;

  return (
    <div
      className={cn(
        "rounded-xl border border-transparent px-1 py-1.5 transition-colors",
        expanded && "border-border/60 bg-background/40 p-3",
        !question.active && "opacity-50",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-col">
          <button
            type="button"
            disabled={!canMoveUp}
            onClick={() => onMove(-1)}
            className="text-muted-foreground/40 hover:text-foreground disabled:opacity-20"
            aria-label="Move question up"
          >
            <ChevronUp className="size-3" />
          </button>
          <button
            type="button"
            disabled={!canMoveDown}
            onClick={() => onMove(1)}
            className="text-muted-foreground/40 hover:text-foreground disabled:opacity-20"
            aria-label="Move question down"
          >
            <ChevronDown className="size-3" />
          </button>
        </div>

        <LiveInput
          value={question.prompt}
          onCommit={(prompt) => onPatch({ prompt })}
          className="h-8 min-w-0 flex-1 text-[14px]"
          aria-label="Question prompt"
        />

        <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          weight
          <select
            value={question.weight}
            onChange={(e) => onPatch({ weight: Number(e.target.value) })}
            className="h-8 rounded-md border border-input bg-background/60 px-1.5 text-[12.5px]"
          >
            {[0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4].map((w) => (
              <option key={w} value={w}>
                ×{w}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={() => onPatch({ active: !question.active })}
          title={question.active ? "Active" : "Inactive — not shown, not scored"}
          className={cn(
            "rounded-full px-2.5 py-1 text-[11.5px] font-medium transition-colors",
            question.active
              ? "bg-brand/10 text-brand"
              : "bg-muted text-muted-foreground",
          )}
        >
          {question.active ? "active" : "off"}
        </button>

        {linked && (
          <span
            title={`Paired with “${linked.prompt}” — each is scored against the other, not against itself`}
            className="inline-flex min-w-0 items-center gap-1 rounded-full bg-brand/10 px-2 py-1 text-[11.5px] font-medium text-brand"
          >
            <Link2 className="size-3 shrink-0" />
            <span className="max-w-36 truncate">{linked.prompt}</span>
          </span>
        )}

        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          className="rounded-full px-2 py-1 text-[12px] text-muted-foreground hover:bg-accent"
        >
          {expanded ? "less" : "more"}
        </button>

        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete question"
          className="grid size-7 place-items-center rounded-full text-muted-foreground/40 hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="mt-3 grid gap-3 pl-6 sm:grid-cols-2">
          <label className="text-[12px] text-muted-foreground sm:col-span-2">
            Phrasing
            <select
              value={question.ladder}
              onChange={(e) => onPatch({ ladder: e.target.value })}
              className="mt-1 h-8 w-full rounded-md border border-input bg-background/60 px-2 text-[13px]"
            >
              {LADDER_IDS.map((id) => (
                <option key={id} value={id}>
                  {LADDERS[id].label}
                </option>
              ))}
            </select>
            {/* The seven stops, in order, so the choice can be judged before
                it's made rather than by opening the profile page. */}
            <span className="mt-1.5 flex flex-wrap items-center gap-1">
              {ladderOf(question.ladder).steps.map((step, i) => (
                <span
                  key={step}
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[11.5px] tabular-nums",
                    i === 3
                      ? "bg-muted text-muted-foreground"
                      : i < 3
                        ? "bg-negative/10 text-negative"
                        : "bg-positive/10 text-positive",
                  )}
                  title={`${i - 3 > 0 ? "+" : ""}${i - 3}`}
                >
                  {step}
                </span>
              ))}
            </span>
          </label>
          <label className="text-[12px] text-muted-foreground sm:col-span-2">
            Help text
            <Textarea
              defaultValue={question.help}
              onBlur={(e) =>
                e.target.value !== question.help && onPatch({ help: e.target.value })
              }
              placeholder="Optional clarification shown under the prompt"
              className="mt-1 min-h-16 bg-background/60 text-[13px]"
            />
          </label>
          <label className="text-[12px] text-muted-foreground sm:col-span-2">
            Move to group
            <select
              value={question.groupId}
              onChange={(e) => onPatch({ groupId: e.target.value })}
              className="mt-1 h-8 w-full rounded-md border border-input bg-background/60 px-2 text-[13px]"
            >
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.emoji} {g.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-[12px] text-muted-foreground sm:col-span-2">
            Paired with
            <select
              value={question.linkedQuestionId ?? ""}
              onChange={(e) => onLink(e.target.value || null)}
              className="mt-1 h-8 w-full rounded-md border border-input bg-background/60 px-2 text-[13px]"
            >
              <option value="">— not paired —</option>
              {groups.map((g) => {
                const options = allQuestions.filter(
                  (q) => q.groupId === g.id && q.id !== question.id,
                );
                if (options.length === 0) return null;
                return (
                  <optgroup key={g.id} label={`${g.emoji} ${g.label}`}>
                    {options.map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.prompt}
                        {/* Choosing one of these takes it from whatever it's
                            currently paired with, so say so before the click. */}
                        {q.linkedQuestionId && q.linkedQuestionId !== question.id
                          ? " — already paired"
                          : ""}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            <span className="mt-1.5 block text-[11.5px] leading-relaxed">
              For complements rather than agreements: a paired question is scored against the
              other person's answer to its partner instead of to itself, so "taking the lead"
              meets "being led". Mutual and 1:1 — setting it here sets it on both, and takes
              the partner away from anything else it was paired with.
            </span>
          </label>
        </div>
      )}
    </div>
  );
}
